import { useDebouncedValue } from '@mantine/hooks'
import { isEmpty } from 'lodash-es'
import { useEffect, useState } from 'react'

import {
  SORT_FILTER_TRAIT_DATERANGE,
  SORT_FILTER_TRAIT_FULLTEXT,
  SORT_FILTER_TRAIT_MULTIVALUE,
  SORT_FILTER_TRAIT_THREE_SIDED_BOOLEAN,
  SORT_FILTER_TRAIT_UNRESTRICTED_VALUE,
  SORT_FILTER_TRAIT_VALUE,
} from '../../constants'
import {
  DateRangeFilterValue,
  FilterType,
  MantineDateRange,
  UseDebouncedFilterValue,
  UseDebouncedFilterValueParams,
} from './use-debounced-filter-value'
import {
  getInitialValue,
  isValidNumber,
  isValidString,
  mantineToDateRange,
} from './utils'

const useDebouncedFilterValue = <T>({
  filterDefinition,
  sortFilter,
}: UseDebouncedFilterValueParams): UseDebouncedFilterValue<T> => {
  const [value, setValue] = useState<T>(() =>
    getInitialValue<T>({
      filterDefinition,
      activeFilters: sortFilter.activeFilters,
    }),
  )

  useEffect(() => {
    setValue(
      getInitialValue<T>({
        filterDefinition,
        activeFilters: sortFilter.activeFilters,
      }),
    )
  }, [sortFilter.initialFilters])

  const [debouncedValue] = useDebouncedValue(value, 500)

  useEffect(() => {
    sortFilter.setActiveFilterValue({
      field:
        filterDefinition.type === SORT_FILTER_TRAIT_FULLTEXT
          ? SORT_FILTER_TRAIT_FULLTEXT
          : filterDefinition.name,
      value: fixValueToBeDispatched({
        debouncedValue,
        filterType: filterDefinition.type,
      }),
    })
  }, [debouncedValue])

  useEffect(() => {
    if (typeof sortFilter.filterResetTimeStamp === 'number') {
      setValue(
        getInitialValue<T>({
          filterDefinition,
          activeFilters: sortFilter.activeFilters,
        }),
      )
    }
  }, [sortFilter.filterResetTimeStamp])

  return {
    value,
    onChange: setValue,
    debouncedValue,
  }
}

export default useDebouncedFilterValue

export const fixValueToBeDispatched = <T>({
  debouncedValue,
  filterType,
}: {
  debouncedValue: T
  filterType: FilterType
}): T => {
  if (
    filterType === SORT_FILTER_TRAIT_FULLTEXT ||
    filterType === SORT_FILTER_TRAIT_VALUE
  ) {
    return !debouncedValue || typeof debouncedValue !== 'string'
      ? ('' as T)
      : (debouncedValue as T)
  }

  if (filterType === SORT_FILTER_TRAIT_DATERANGE) {
    const dateRangeFilter: DateRangeFilterValue =
      !Array.isArray(debouncedValue) || debouncedValue.length !== 2
        ? { startDate: null, endDate: null }
        : mantineToDateRange(debouncedValue as MantineDateRange)

    return dateRangeFilter as T
  }

  if (filterType === SORT_FILTER_TRAIT_THREE_SIDED_BOOLEAN) {
    return (
      isEmpty(debouncedValue) || typeof debouncedValue !== 'string'
        ? SORT_FILTER_TRAIT_UNRESTRICTED_VALUE
        : debouncedValue
    ) as T
  }

  if (filterType === SORT_FILTER_TRAIT_MULTIVALUE) {
    const multiValue = !Array.isArray(debouncedValue)
      ? []
      : debouncedValue
          .map((v) => {
            const isIntegerString = /^\d+$/.test(v)
            if (isIntegerString) {
              return parseInt(v, 10)
            } else if (typeof v === 'string') {
              return v
            } else {
              return undefined
            }
          })
          .filter(
            (v) =>
              (isValidString(v) || isValidNumber(v)) &&
              typeof v !== 'undefined',
          )
    return multiValue as T
  }

  return debouncedValue
}
